import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MapPin, LogIn, LogOut, Clock, History } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MyAttendanceWidget() {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/hr/attendance/my-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch (e) {
      // silent — widget just won't render
    } finally {
      setLoading(false);
    }
  };

  const getPosition = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        return reject(new Error("Geolocation is not supported by your browser"));
      }
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy_m: pos.coords.accuracy,
          }),
        (err) => {
          const map = {
            1: "Location access denied. Please enable location in your browser settings.",
            2: "Location unavailable. Please check your device GPS.",
            3: "Location request timed out. Please try again.",
          };
          reject(new Error(map[err.code] || err.message));
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });

  const doCheck = async (kind) => {
    setBusy(true);
    try {
      toast.info("Fetching your location…");
      const coords = await getPosition();
      const res = await axios.post(`${API}/hr/attendance/${kind}`, coords, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message);
      fetchStatus();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Action failed";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    try {
      const res = await axios.get(`${API}/hr/attendance/my-records?days=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data.records || []);
    } catch (e) {
      toast.error("Failed to load history");
    }
  };

  if (loading) return null;
  // Don't show widget for users without an employee record (e.g., system admins)
  if (!status?.has_employee_record) return null;

  // Normalize placeholder values from legacy data ("" / "00:00") as not-yet-set.
  const isRealTime = (v) => !!v && v !== "00:00";
  const checkInDisplay = isRealTime(status.check_in) ? status.check_in : "—";
  const checkOutDisplay = isRealTime(status.check_out) ? status.check_out : "—";
  const onLeave = !!status.on_leave;
  const checkedIn = isRealTime(status.check_in);
  const checkedOut = isRealTime(status.check_out);

  return (
    <Card
      className="p-6 border-slate-200 dark:border-slate-700 rounded-xl shadow-sm bg-white dark:bg-slate-800 mb-6"
      data-testid="my-attendance-widget"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Clock className="h-5 w-5 text-brillien-600" />
            My Attendance
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Check in / out using your current GPS location
          </p>
        </div>
        <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={openHistory}
              data-testid="view-attendance-history-btn"
            >
              <History className="h-4 w-4 mr-2" /> View history
            </Button>
          </DialogTrigger>
          <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-3xl">
            <DialogHeader>
              <DialogTitle>My Attendance — Last 30 Days</DialogTitle>
              <DialogDescription>
                Your recent check-in / check-out history.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-700 sticky top-0">
                  <tr>
                    <th className="text-left p-2 dark:text-slate-200">Date</th>
                    <th className="text-left p-2 dark:text-slate-200">In</th>
                    <th className="text-left p-2 dark:text-slate-200">Out</th>
                    <th className="text-left p-2 dark:text-slate-200">Location</th>
                    <th className="text-left p-2 dark:text-slate-200">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-4 text-center text-slate-500">
                        No attendance records found.
                      </td>
                    </tr>
                  )}
                  {history.map((r) => (
                    <tr key={r.id} className="border-t dark:border-slate-700">
                      <td className="p-2 dark:text-slate-200">{r.date}</td>
                      <td className="p-2 dark:text-slate-200">{isRealTime(r.check_in) ? r.check_in : "—"}</td>
                      <td className="p-2 dark:text-slate-200">{isRealTime(r.check_out) ? r.check_out : "—"}</td>
                      <td className="p-2 dark:text-slate-200 text-xs">
                        {r.check_in_location_name || r.location || "—"}
                      </td>
                      <td className="p-2">
                        <Badge variant="outline" className="text-xs">
                          {r.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-600">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Today's Check-In</p>
          <p className="font-mono text-lg font-semibold text-slate-900 dark:text-white">
            {checkInDisplay}
          </p>
          {status.check_in_location_name && (
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {status.check_in_location_name}
            </p>
          )}
        </div>
        <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-600">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Today's Check-Out</p>
          <p className="font-mono text-lg font-semibold text-slate-900 dark:text-white">
            {checkOutDisplay}
          </p>
          {status.check_out_location_name && (
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {status.check_out_location_name}
            </p>
          )}
        </div>
        <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 flex items-center">
          <Badge
            className={
              onLeave
                ? "bg-blue-100 text-blue-700"
                : checkedOut
                ? "bg-slate-200 text-slate-700"
                : checkedIn
                ? "bg-emerald-100 text-emerald-700"
                : "bg-amber-100 text-amber-700"
            }
          >
            {onLeave
              ? `On Leave${status.leave_type ? ` (${status.leave_type})` : ""}`
              : checkedOut ? "Day Complete" : checkedIn ? "Checked In" : "Not Checked In"}
          </Badge>
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          onClick={() => doCheck("check-in")}
          disabled={busy || checkedIn || onLeave}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="geo-check-in-btn"
        >
          <LogIn className="h-4 w-4 mr-2" /> Check In
        </Button>
        <Button
          onClick={() => doCheck("check-out")}
          disabled={busy || !checkedIn || checkedOut || onLeave}
          className="bg-brillien-600 hover:bg-brillien-700 text-white"
          data-testid="geo-check-out-btn"
        >
          <LogOut className="h-4 w-4 mr-2" /> Check Out
        </Button>
      </div>
      {onLeave ? (
        <p className="text-xs text-blue-600 dark:text-blue-400 mt-3">
          You are on approved leave today. Check-in / out is disabled.
        </p>
      ) : (
        <p className="text-xs text-slate-400 mt-3">
          Your browser will ask for location permission. Geolocation requires HTTPS and an
          approved attendance location nearby.
        </p>
      )}
    </Card>
  );
}
